import React, { useRef, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import MicNoneIcon from "@mui/icons-material/MicNone";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import ChatbotIcon from "../../ChatbotIcon";
import { primaryColor } from "../../../Theme";

const demoVideo = require("../../../video/h2h-digitalrx-video-ecsribeagent.mp4") as string;

interface AgenticRxFeatureAccessProps {
  clinicAccount?: unknown;
  primaryAdmin?: unknown;
  contactEmail?: unknown;
}

const BENEFITS = [
  { icon: MicNoneIcon, text: "Voice, transcript, or text" },
  { icon: AutoAwesomeOutlinedIcon, text: "Medication & pharmacy suggestions" },
  { icon: LocalPharmacyOutlinedIcon, text: "Send to pharmacy in-flow" },
];

function asText(value: unknown, fallback: string): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || fallback;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return fallback;
}

const ctaSx = {
  width: "100%",
  maxWidth: "100%",
  height: "auto !important",
  minHeight: 44,
  whiteSpace: "normal !important",
  lineHeight: 1.3,
  textTransform: "none",
  fontWeight: 700,
  fontSize: "0.9rem",
  py: 1.1,
  px: 1.5,
  color: "#fff !important",
  backgroundColor: `${primaryColor} !important`,
  boxShadow: `0 8px 18px ${primaryColor}33`,
  flex: "none",
  "&:hover": {
    color: "#fff !important",
    backgroundColor: `${primaryColor} !important`,
    boxShadow: `0 10px 22px ${primaryColor}44`,
  },
  "& .MuiButton-startIcon, & .MuiSvgIcon-root": {
    color: "#fff !important",
  },
} as const;

/** Locked-state screen: contacting the clinic admin is the primary action. */
const AgenticRxFeatureAccess: React.FC<AgenticRxFeatureAccessProps> = ({
  clinicAccount,
  primaryAdmin,
  contactEmail,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showPlayOverlay, setShowPlayOverlay] = useState(true);
  const clinicText = asText(clinicAccount, "Your clinic");
  const adminText = asText(primaryAdmin, "Clinic administrator");
  const emailText = asText(contactEmail, "");
  const mailHref = emailText
    ? `mailto:${emailText}?subject=${encodeURIComponent("Request to Enable AgenticRx Feature")}`
    : "";

  const startDemo = () => {
    const video = videoRef.current;
    if (!video) return;
    setShowPlayOverlay(false);
    void video.play();
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100%",
        height: { xs: "auto", md: "100%" },
        minHeight: { xs: "auto", md: 0 },
        minWidth: 0,
        flex: { xs: "0 0 auto", md: "1 1 auto" },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 1.1, md: 1.25 },
        overflow: { xs: "visible", md: "hidden" },
        boxSizing: "border-box",
        px: { xs: 0, sm: 1 },
        pb: { xs: 1, md: 0 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          minWidth: 0,
          flex: "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: { xs: 36, sm: 42 },
            height: { xs: 36, sm: 42 },
            borderRadius: "50%",
            flex: "none",
            background: `radial-gradient(circle at 32% 28%, ${primaryColor}ee, ${primaryColor})`,
            boxShadow: `0 4px 12px ${primaryColor}28`,
          }}
        >
          <ChatbotIcon color="#ffffff" style={{ width: 18, height: 18 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            component="h2"
            sx={{ color: primaryColor, fontWeight: 700, fontSize: { xs: "1rem", sm: "1.12rem" }, lineHeight: 1.2 }}
          >
            AgenticRx AI Assistant
          </Typography>
          <Typography component="div" sx={{ color: `${primaryColor}aa`, fontSize: { xs: "0.72rem", sm: "0.8rem" } }}>
            Not enabled for this account — contact your administrator to turn it on
          </Typography>
        </Box>
        <Box
          sx={{
            flex: "none",
            px: 1.1,
            py: 0.4,
            borderRadius: "999px",
            backgroundColor: "#fde8e8",
            color: "#b42318",
            fontSize: "0.68rem",
            fontWeight: 700,
            letterSpacing: "0.02em",
          }}
        >
          Inactive
        </Box>
      </Box>

      <Box
        sx={{
          display: { xs: "flex", md: "grid" },
          flexDirection: "column",
          gridTemplateColumns: { md: "minmax(280px, 1.45fr) minmax(0, 1fr)" },
          gridTemplateRows: { md: "minmax(0, 1fr)" },
          gap: { xs: 1.15, md: 1.35 },
          minHeight: 0,
          minWidth: 0,
          flex: { xs: "0 0 auto", md: "1 1 auto" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: { xs: 0.85, md: 0.75 },
            minHeight: { xs: "auto", md: 0 },
            height: { md: "100%" },
            minWidth: 0,
            border: `1px solid ${primaryColor}33`,
            borderLeft: { xs: `1px solid ${primaryColor}33`, md: `5px solid ${primaryColor}` },
            borderRadius: "14px",
            p: { xs: 1.15, sm: 1.25 },
            background: `linear-gradient(180deg, ${primaryColor}12 0%, ${primaryColor}08 40%, #fff 100%)`,
            order: { xs: 1, md: 0 },
            overflow: "hidden",
            boxShadow: `0 8px 24px ${primaryColor}14`,
          }}
        >
          <Typography
            component="div"
            sx={{
              fontSize: { xs: "0.88rem", sm: "0.96rem" },
              fontWeight: 800,
              color: primaryColor,
              lineHeight: 1.2,
              flex: "none",
            }}
          >
            Ask your administrator to enable AgenticRx
          </Typography>
          <Typography
            component="div"
            sx={{
              fontSize: "0.75rem",
              lineHeight: 1.3,
              color: "text.primary",
              flex: "none",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            Voice prescribing, suggestions, and send-to-pharmacy stay off until this is turned on.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.85,
              p: 0.9,
              borderRadius: "10px",
              backgroundColor: "#fff",
              border: `1px solid ${primaryColor}22`,
              flex: "none",
              minWidth: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.85, minWidth: 0 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  flex: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${primaryColor}18`,
                  color: primaryColor,
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  component="div"
                  sx={{ fontSize: "0.58rem", fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}
                >
                  Primary admin
                </Typography>
                <Typography
                  component="div"
                  title={adminText}
                  sx={{
                    fontSize: "0.8rem",
                    fontWeight: 800,
                    color: "text.primary",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                  }}
                >
                  {adminText}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.85, minWidth: 0 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  flex: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: `${primaryColor}18`,
                  color: primaryColor,
                }}
              >
                <ApartmentOutlinedIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  component="div"
                  sx={{ fontSize: "0.58rem", fontWeight: 700, color: "text.secondary", textTransform: "uppercase" }}
                >
                  Clinic account
                </Typography>
                <Typography
                  component="div"
                  title={clinicText}
                  sx={{
                    fontSize: "0.74rem",
                    fontWeight: 700,
                    color: "text.primary",
                    lineHeight: 1.35,
                    wordBreak: "break-word",
                  }}
                >
                  {clinicText}
                </Typography>
                <Typography
                  component="div"
                  title={emailText || "Email not available"}
                  sx={{
                    fontSize: "0.7rem",
                    color: `${primaryColor}cc`,
                    mt: 0.15,
                    wordBreak: "break-word",
                  }}
                >
                  {emailText || "Email not available"}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.85,
              flex: "none",
              overflow: "hidden",
            }}
          >
            {BENEFITS.map(({ icon: Icon, text }) => (
              <Box
                key={text}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  minWidth: 0,
                  maxWidth: "100%",
                  color: primaryColor,
                  px: 0.7,
                  py: 0.35,
                  borderRadius: "999px",
                  backgroundColor: `${primaryColor}12`,
                }}
              >
                <Icon sx={{ fontSize: 13, flex: "none" }} />
                <Typography
                  component="span"
                  sx={{
                    fontSize: "0.68rem",
                    fontWeight: 600,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {text}
                </Typography>
              </Box>
            ))}
          </Box>
          
          <Button
            type="button"
            variant="contained"
            color="primary"
            startIcon={<MailOutlineIcon sx={{ color: "#fff" }} />}
            disabled={!mailHref}
            onClick={() => {
              if (mailHref) window.location.href = mailHref;
            }}
            sx={{ ...ctaSx, minHeight: 40, py: 0.9 }}
          >
            Email administrator to enable
          </Button>

        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            width: "100%",
            minHeight: { md: 0 },
            height: { md: "100%" },
            borderRadius: "14px",
            overflow: "hidden",
            backgroundColor: "#fff",
            colorScheme: "light",
            order: { xs: 2, md: 0 },
            border: `1px solid ${primaryColor}33`,
            boxShadow: `0 8px 24px ${primaryColor}14`,
            gap: 0,
          }}
        >
          <Box
            sx={{
              px: 1.25,
              py: 0.55,
              flex: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              backgroundColor: `${primaryColor}0c`,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="div"
                sx={{ fontSize: "0.8rem", fontWeight: 800, color: primaryColor, lineHeight: 1.2 }}
              >
                Watch AgenticRx demo
              </Typography>
              <Typography component="div" sx={{ fontSize: "0.68rem", color: `${primaryColor}aa`, mt: 0.15 }}>
                See voice prescribing in action
              </Typography>
            </Box>
            <Box
              sx={{
                flex: "none",
                px: 0.85,
                py: 0.25,
                borderRadius: "999px",
                backgroundColor: `${primaryColor}14`,
                color: primaryColor,
                fontSize: "0.62rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                border: `1px solid ${primaryColor}33`,
              }}
            >
              Demo
            </Box>
          </Box>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              flex: "none",
              aspectRatio: "16 / 9",
              maxHeight: { xs: "42dvh", md: "100%" },
              backgroundColor: "#f4f7fb",
              colorScheme: "light",
              lineHeight: 0,
              "@media (orientation: landscape) and (max-width: 899.95px)": {
                width: "min(100%, calc(62dvh * 16 / 9))",
                maxHeight: "62dvh",
                mx: "auto",
              },
              "& video": {
                opacity: 0.6,
                transition: "opacity 0.25s ease",
              },
              "&:hover video": {
                opacity: 1,
              },
            }}
          >
            <video
              ref={videoRef}
              src={demoVideo}
              controls={!showPlayOverlay}
              playsInline
              preload="metadata"
              onPlay={() => setShowPlayOverlay(false)}
              onEnded={() => setShowPlayOverlay(true)}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                objectPosition: "top center",
                backgroundColor: "#f4f7fb",
                display: "block",
              }}
            />
            {showPlayOverlay && (
              <Box
                component="button"
                type="button"
                onClick={startDemo}
                aria-label="Play AgenticRx demo"
                sx={{
                  all: "unset",
                  boxSizing: "border-box",
                  position: "absolute",
                  inset: 0,
                  zIndex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  cursor: "pointer",
                  backgroundColor: "transparent",
                  color: primaryColor,
                  "&:hover .playBtn": {
                    transform: "scale(1.06)",
                  },
                  "&:focus-visible": {
                    outline: `2px solid ${primaryColor}`,
                    outlineOffset: -4,
                  },
                }}
              >
                <Box
                  className="playBtn"
                  sx={{
                    width: 62,
                    height: 62,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: primaryColor,
                    boxShadow: `0 10px 24px ${primaryColor}44, 0 0 0 8px ${primaryColor}18`,
                    transition: "transform 0.15s ease",
                    color: "#fff",
                  }}
                >
                  <PlayArrowRoundedIcon sx={{ fontSize: 38 }} />
                </Box>
                <Typography component="div" sx={{ fontSize: "0.82rem", fontWeight: 700, color: primaryColor }}>
                  Play demo
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        
      </Box>
    </Box>
  );
};

export default AgenticRxFeatureAccess;
